import axios, { isAxiosError, type AxiosInstance, type AxiosRequestConfig } from 'axios';
import * as v from 'valibot';
import { API_CONFIG, API_ERROR_MESSAGES, HTTP_STATUS } from '../constants/api';
import type { ApiError } from '../types/apiError';

export const normalizeAxiosError = (err: unknown): ApiError => {
  if (!isAxiosError(err)) {
    return {
      message: API_ERROR_MESSAGES.NETWORK_ERROR,
      isNetworkError: true,
    };
  }

  if (err.code === 'ECONNABORTED') {
    return {
      message: API_ERROR_MESSAGES.TIMEOUT_ERROR,
      isNetworkError: true,
      original: err,
    };
  }

  if (!err.response) {
    return {
      message: API_ERROR_MESSAGES.NETWORK_ERROR,
      isNetworkError: true,
      original: err,
    };
  }

  const status = err.response.status;
  const data = err.response.data;

  if (data && typeof data === 'object' && 'error' in data) {
    return {
      status,
      message: String((data as { error: unknown }).error),
      isNetworkError: false,
      original: err,
    };
  }

  const messageByStatus: Partial<Record<number, string>> = {
    [HTTP_STATUS.BAD_REQUEST]: API_ERROR_MESSAGES.VALIDATION_ERROR,
    [HTTP_STATUS.UNAUTHORIZED]: API_ERROR_MESSAGES.UNAUTHORIZED,
    [HTTP_STATUS.FORBIDDEN]: API_ERROR_MESSAGES.FORBIDDEN,
    [HTTP_STATUS.NOT_FOUND]: API_ERROR_MESSAGES.NOT_FOUND,
    [HTTP_STATUS.TIMEOUT]: API_ERROR_MESSAGES.TIMEOUT_ERROR,
    [HTTP_STATUS.INTERNAL_SERVER_ERROR]: API_ERROR_MESSAGES.SERVER_ERROR,
    [HTTP_STATUS.BAD_GATEWAY]: API_ERROR_MESSAGES.SERVER_ERROR,
    [HTTP_STATUS.SERVICE_UNAVAILABLE]: API_ERROR_MESSAGES.SERVER_ERROR,
  };

  return {
    status,
    message: messageByStatus[status] ?? API_ERROR_MESSAGES.SERVER_ERROR,
    isNetworkError: false,
    original: err,
  };
};

const logApiError = (apiError: ApiError): void => {
  if (apiError.status !== undefined) {
    console.error(apiError.status, apiError.message);
    return;
  }
  console.error(apiError.message);
};

export const apiClient: AxiosInstance = axios.create({
  baseURL: `${API_CONFIG.BASE_URL}/${API_CONFIG.VERSION}`,
  timeout: API_CONFIG.TIMEOUT,
  // Send the `sa_session` cookie on cross-origin calls (www.sorareace.com -> api.sorareace.com,
  // and localhost:3000 -> :3007 in dev). Requires `credentials: true` on the API's CORS config —
  // deploy the API first, or the browser blocks *every* response, not just the auth ones.
  withCredentials: true,
});

apiClient.interceptors.request.use((config) => {
  // Single place for auth headers when the API adds authentication.
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    // A request aborted via AbortSignal (React Query cancels superseded/unmounted
    // queries) rejects with a CanceledError. Rethrow it as-is: it must not be logged
    // as an error or normalized to a network ApiError. React Query discards the result.
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }
    const apiError = normalizeAxiosError(error);
    logApiError(apiError);
    return Promise.reject(apiError);
  },
);

const getRequest = async <T>(path: string, config?: AxiosRequestConfig): Promise<T> => {
  const result = await apiClient.get<T>(path, config);
  return result.data;
};

const postRequest = async <T>(path: string, config?: AxiosRequestConfig): Promise<T> => {
  // `config` is axios's third argument, not the second — passing it as the second sends it
  // as the request *body* and silently drops the config (signal, headers, ...). Every current
  // caller passes no config, so this was latent; `authAPI.logout({ signal })` is the first
  // that would have hit it. Our POSTs carry no body today, hence the explicit `undefined`.
  const result = await apiClient.post<T>(path, undefined, config);
  return result.data;
};

/**
 * GET a response and validate it against a valibot schema at the network boundary,
 * returning it typed as the caller's curated DTO `T` (a *validated* cast).
 *
 * The runtime schema and the compile-time type `T` are intentionally decoupled: the
 * schemas in `src/schemas/` use `looseObject` (so undeclared keys the UI reads survive)
 * and `nullish` leaves (the API sends `null` for nullable columns), which makes their
 * raw `InferOutput` too loose to expose to consumers (index signatures, `| null`
 * unions). Consumers instead code against the hand-written DTOs in `src/types/api.ts`;
 * the schema's job is purely to reject a malformed *envelope* (non-object, non-array
 * `items`, missing required container, wrong-typed declared field) at the boundary.
 *
 * Real transport failures flow through `getRequest` (and the axios interceptor →
 * `normalizeAxiosError`) unchanged. A schema mismatch rejects with the same `ApiError`
 * shape so `QueryErrorState` renders it with zero changes to hooks or containers; the
 * detailed valibot issues are logged for debugging while the user-facing message stays generic.
 */
const getValidated = async <T>(
  path: string,
  schema: v.GenericSchema,
  config?: AxiosRequestConfig,
): Promise<T> => {
  const data = await getRequest<unknown>(path, config);
  const result = v.safeParse(schema, data);
  if (!result.success) {
    console.error('Response validation failed:', path, v.flatten(result.issues));
    return Promise.reject({
      message: API_ERROR_MESSAGES.PARSE_ERROR,
      isNetworkError: false,
    } satisfies ApiError);
  }
  return result.output as T;
};

export { getRequest, postRequest, getValidated };
